import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { CustomComponent } from './types'; // Assuming this type is defined elsewhere
import { ChevronDown, ChevronUp } from 'lucide-react';

export const Pre: CustomComponent = ({ node, children, ...props }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);

  const checkOverflow = useCallback(() => {
    if (preRef.current) {
      // Check if scrollHeight (total content height) is greater than maxHeight (visible area)
      // Or a fixed height like 300px. Let's stick to a fixed height for the collapse trigger.
      const contentHeight = preRef.current.scrollHeight;
      const visibleHeight = 300; // The height at which we consider it "tall enough"

      const needsCollapseButton = contentHeight > visibleHeight;
      setHasOverflow(needsCollapseButton);

      // If it was previously not overflowing and now is, or vice-versa,
      // set initial collapsed state.
      // Avoid collapsing if it's already expanded by user.
      // This logic might need adjustment based on desired behavior when content changes.
      if (needsCollapseButton && !hasOverflow) { // Only set to collapsed if it *newly* needs it
        setIsCollapsed(true);
      } else if (!needsCollapseButton) {
        setIsCollapsed(false); // No overflow, so ensure it's not collapsed
      }
    }
  }, [hasOverflow]);

  // Check on initial render and when children change
  useEffect(() => {
    checkOverflow();
  }, [children, checkOverflow]);

  // Also check on window resize, as this can affect overflow if width changes pre-wrap behavior
  useEffect(() => {
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow]);


  // Adjusted maxHeight to leave space for the button when collapsed
  const preMaxHeight = isCollapsed ? '280px' : '2000px'; // Slightly less for button
  const prePaddingBottom = isCollapsed && hasOverflow ? '40px' : '16px'; // 1rem default (p-4), more if collapsed

  return (
    <div className="relative my-4 w-full"> {/* Removed style={{ display: 'block', overflow: 'visible' }} as it's default or not needed */}
      <pre
        ref={preRef}
        {...props}
        className="custom-pre-block" // Apply the main styles via class
        style={{
          maxHeight: preMaxHeight,
          paddingBottom: prePaddingBottom,
        }}
      >
        {children}
      </pre>

      {hasOverflow && (
        <div
          className="absolute bottom-0 left-0 right-0 flex justify-center items-end"
          // Adjust pb-1 or similar to fine-tune vertical position of the button from the very bottom of the parent div
          style={{ paddingBottom: '4px' }} // Example: small padding from bottom edge of the container
        >
          {/* Gradient shown only when collapsed */}
          <div
            className={`absolute bottom-0 left-0 right-0 h-20 pointer-events-none ${ // Increased height for a smoother gradient
              isCollapsed ? 'block' : 'hidden'
            }`}
            style={{
              background: 'linear-gradient(to top, #f8f9fc 60%, transparent)', // Adjusted gradient
              // Ensure gradient respects the pre's border radius if any part of it is inside
              // This is tricky as the gradient is outside the pre.
              // For perfect masking, the gradient might need to be a pseudo-element of the <pre> or an overlay inside it.
              // Or, ensure the parent div has the same background color at the edges.
            }}
          />
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            // The button sits on top of the gradient (z-10)
            className="relative px-3 py-1 text-xs bg-slate-100 text-slate-600 rounded border border-slate-200 hover:bg-slate-200 z-10 shadow-md"
          >
            <span className="flex items-center gap-1">
              {isCollapsed ? (
                <>Show more <ChevronDown className="w-3 h-3" /></>
              ) : (
                <>Show less <ChevronUp className="w-3 h-3" /></>
              )}
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default Pre;